
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

export interface RealtimeNotification {
  id: string;
  user_id: string;
  type: string;
  titre: string;
  message: string;
  priorite: string | null;
  statut: string | null;
  lien: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  read_at: string | null;
}

/**
 * Custom hook that provides real‑time notifications for a given user.
 * It handles:
 *  - initial fetch,
 *  - real‑time INSERT / UPDATE events,
 *  - optimistic UI updates for read / delete actions,
 *  - error handling & rollback.
 *
 * @param userId The id of the logged‑in user (or undefined while loading)
 */
export function useRealtimeNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<RealtimeNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /** Fetch the latest 50 notifications for the current user */
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, type, titre, message, priorite, statut, lien, metadata, created_at, read_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        logger.error('Error loading notifications:', error.message);
        return;
      }

      const items = (data ?? []) as RealtimeNotification[];
      setNotifications(items);
      setUnreadCount(items.filter((n) => !n.read_at).length);
    } catch (err) {
      logger.error('Unexpected error while fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /** Subscribe to real‑time changes (INSERT & UPDATE) */
  useEffect(() => {
    if (!userId) return;

    // Load initial data
    fetchNotifications();

    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotif = payload.new as RealtimeNotification;
          setNotifications((prev) => [newNotif, ...prev].slice(0, 50));
          setUnreadCount((prev) => prev + 1);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new as RealtimeNotification;
          setNotifications((prev) =>
            prev.map((n) => (n.id === updated.id ? updated : n)),
          );
          // Re‑calculate unread count after the update
          setUnreadCount((prev) =>
            prev - (updated.read_at ? 1 : 0) + (updated.read_at ? 0 : 1),
          );
        },
      )
      .subscribe();

    channelRef.current = channel;

    // Cleanup on unmount or user change
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, fetchNotifications]);

  /** Mark a single notification as read (optimistic UI) */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const now = new Date().toISOString();

      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: now } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: now })
        .eq('id', notificationId);

      if (error) {
        logger.error('Error marking notification as read:', error.message);
        // Rollback to server state
        await fetchNotifications();
      }
    },
    [fetchNotifications],
  );

  /** Mark all notifications as read (optimistic UI) */
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    const now = new Date().toISOString();

    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (!n.read_at ? { ...n, read_at: now } : n)),
    );
    setUnreadCount(0);

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: now })
      .eq('user_id', userId)
      .is('read_at', null);

    if (error) {
      logger.error('Error marking all notifications as read:', error.message);
      await fetchNotifications(); // rollback
    }
  }, [userId, fetchNotifications]);

  /** Delete a notification (optimistic UI) */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      const target = notifications.find((n) => n.id === notificationId);

      // Optimistic update
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (target && !target.read_at) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        logger.error('Error deleting notification:', error.message);
        await fetchNotifications(); // rollback
      }
    },
    [notifications, fetchNotifications],
  );

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
  };
}
